<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from '@/composables/useI18n'

const { lang, t } = useI18n()

const isOpen = ref(false)
const content = ref('')
const loaded = ref(false)

async function open() {
  if (!loaded.value) {
    try {
      const response = await fetch(`/data-privacy-${lang.value}.md`)
      if (!response.ok) throw new Error('Failed to load')
      const { renderMarkdown } = await import('@/markdown')
      content.value = renderMarkdown(await response.text())
      loaded.value = true
    } catch {
      content.value = '<p style="color: var(--bad);">Failed to load privacy policy. Please try again.</p>'
    }
  }
  isOpen.value = true
  document.body.style.overflow = 'hidden'
}

function close() {
  isOpen.value = false
  // Only restore body scroll if subscription modal isn't open
  document.body.style.overflow = ''
}

// Invalidate on language change
import { watch } from 'vue'
watch(lang, () => {
  loaded.value = false
})

// Expose open method globally via event
import { onMounted, onUnmounted } from 'vue'

function handleOpenEvent() {
  open()
}

onMounted(() => {
  document.addEventListener('open-data-privacy-modal', handleOpenEvent)
})

onUnmounted(() => {
  document.removeEventListener('open-data-privacy-modal', handleOpenEvent)
})

defineExpose({ open })
</script>

<template>
  <div v-if="isOpen" id="dataPrivacyModal" class="modal">
    <div class="modal-backdrop" @click="close"></div>
    <div class="modal-dialog modal-lg">
      <div class="modal-header">
        <h3>{{ t('data-privacy-modal-title') }}</h3>
        <button type="button" class="icon-btn" aria-label="Close" @click="close">×</button>
      </div>
      <div id="dataPrivacyContent" class="modal-content data-privacy-content" v-html="content"></div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary" @click="close">
          {{ t('data-privacy-close') }}
        </button>
      </div>
    </div>
  </div>
</template>
